import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowUpRight, Bell, BellOff, CheckCheck, CheckCircle2, Info, OctagonAlert, X } from "lucide-react";
import { useAppServices } from "../hook/services";
import env from "../config";
import { cx } from "../utils";
import { useToast } from "./ui/Toast";

/**
 * The bell in the app page header: notifications the team has sent to this
 * account specifically. New ones arrive live — the badge counts up and a toast
 * says what came in. Read state is per account.
 */

const TONES = {
  info: { icon: Info, tile: "bg-brand-50 text-brand-600 ring-brand-200/70" },
  success: { icon: CheckCircle2, tile: "bg-emerald-50 text-emerald-600 ring-emerald-200/70" },
  warning: { icon: AlertTriangle, tile: "bg-amber-50 text-amber-600 ring-amber-200/80" },
  critical: { icon: OctagonAlert, tile: "bg-rose-50 text-rose-600 ring-rose-200/80" },
};

const ago = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const NotificationBell = ({ appId, locationId, companyId }) => {
  const { notifications } = useAppServices();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  const account = useMemo(
    () => ({ appId, ...(locationId && { locationId }), ...(companyId && { companyId }) }),
    [appId, locationId, companyId],
  );
  const query = useMemo(() => new URLSearchParams(account).toString(), [account]);

  useEffect(() => {
    if (!appId) return undefined;
    let alive = true;
    (async () => {
      const { response } = await notifications.List(query);
      if (!alive) return;
      setItems(Array.isArray(response?.items) ? response.items : []);
      setLoaded(true);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Live: new notifications, and ones the team deleted.
  useEffect(() => {
    if (!appId || !env.API_URL || typeof EventSource === "undefined") return undefined;
    const source = new EventSource(`${env.API_URL}/notifications/stream?${query}`);

    source.addEventListener("notification", (e) => {
      const { notification } = JSON.parse(e.data);
      if (!notification) return;
      setItems((prev) => (prev.some((n) => n._id === notification._id) ? prev : [notification, ...prev]));
      toast(`New notification: ${notification.title}`);
    });
    source.addEventListener("removed", (e) => {
      const { notification } = JSON.parse(e.data);
      setItems((prev) => prev.filter((n) => n._id !== notification?._id));
    });

    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  const markRead = (id) => {
    setItems((prev) => prev.map((n) => (id === "all" || n._id === id ? { ...n, read: true } : n)));
    notifications.Read(id, account);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        className={cx(
          "relative grid h-10 w-10 place-items-center rounded-xl border bg-white shadow-sm transition-all",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
          open ? "border-brand-300 text-brand-600 shadow-glow" : "border-ink-200/80 text-ink-600 hover:border-ink-300 hover:text-ink-900",
        )}
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-gradient-to-br from-rose-500 to-pink-600 px-1 text-[10.5px] font-bold text-white shadow-sm ring-2 ring-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-24 z-50 animate-fade-in overflow-hidden rounded-2xl border border-ink-200/80 bg-white shadow-card-elevated sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 sm:w-[24rem]">
          <div className="flex items-center justify-between gap-2 border-b border-ink-150 px-4 py-3">
            <div className="flex items-baseline gap-2">
              <h3 className="text-[14px] font-semibold text-ink-900">Notifications</h3>
              {unread > 0 && <span className="text-[11.5px] font-medium text-brand-600">{unread} new</span>}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button type="button" onClick={() => markRead("all")} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-800">
                  <CheckCheck size={13} /> Mark all read
                </button>
              )}
              {/* On a phone the panel covers the bell, so it needs its own close. */}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-7 w-7 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700 sm:hidden"
                aria-label="Close notifications"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          <div className="max-h-[min(32rem,70vh)] overflow-y-auto overscroll-contain">
            {!loaded ? (
              <div className="space-y-3 p-4">
                {[0, 1].map((i) => (
                  <div key={i} className="flex gap-3">
                    <div className="h-9 w-9 animate-pulse rounded-xl bg-ink-100" />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className="h-3 w-2/3 animate-pulse rounded bg-ink-100" />
                      <div className="h-3 w-full animate-pulse rounded bg-ink-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <span className="mx-auto grid h-11 w-11 place-items-center rounded-2xl bg-ink-100 text-ink-400">
                  <BellOff size={18} />
                </span>
                <p className="mt-3 text-[13px] font-semibold text-ink-800">You're all caught up</p>
                <p className="mt-0.5 text-[12px] text-ink-500">Messages from the team for your account appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-ink-150">
                {items.map((n) => {
                  const t = TONES[n.tone] || TONES.info;
                  const Icon = t.icon;
                  return (
                    <li key={n._id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => !n.read && markRead(n._id)}
                        onKeyDown={(e) => e.key === "Enter" && !n.read && markRead(n._id)}
                        className={cx("flex gap-3 px-4 py-3.5 transition-colors", n.read ? "hover:bg-ink-50/70" : "bg-brand-50/40 hover:bg-brand-50/70")}
                      >
                        <span className={cx("grid h-9 w-9 shrink-0 place-items-center rounded-xl ring-1", t.tile)}>
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <p className={cx("min-w-0 flex-1 text-[13px] leading-snug", n.read ? "font-medium text-ink-800" : "font-semibold text-ink-900")}>
                              {n.title}
                            </p>
                            {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600" aria-label="Unread" />}
                          </div>
                          {n.message && <p className="mt-0.5 whitespace-pre-line text-[12.5px] leading-relaxed text-ink-600">{n.message}</p>}
                          <div className="mt-2 flex items-center gap-3">
                            <span className="text-[11px] text-ink-400">{ago(n.createdAt)}</span>
                            {n.linkUrl && (
                              <a
                                href={n.linkUrl}
                                // "#support" and the like are tabs on this page: open in
                                // place and close the panel so the tab is in view.
                                {...(n.linkUrl.startsWith("#") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!n.read) markRead(n._id);
                                  if (n.linkUrl.startsWith("#")) setOpen(false);
                                }}
                                className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-brand-600 hover:underline"
                              >
                                {n.linkLabel || "Open"} <ArrowUpRight size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
